import { ClientAPI, ClientAPIWithoutTimeout } from '@/redux/api';
import {
  CreditTrackerViewModel,
  OrganizationMembersResponse,
  UserTrackerData,
  UserTrackerDataRequest
} from '@/@types/client-api';
import { processDownloadedFile } from '@/redux/helpers/fileManagement';

export const rosterBaseUrl = '/roster';
export const creditTrackerAdminRosterBaseUrl = '/credit-tracker-admin/roster';

export interface IRosterMemberQuery {
  orgSk: number;
  indSk: number;
  page?: number;
  size?: number;
  query?: string;
  jurisdictions?: string[];
}

export interface IUserTrackerDataQuery {
  orgSk: number;
  userTrackerDataRequests: UserTrackerDataRequest[];
}

const creditTrackerAdminEndpoints = ClientAPI.injectEndpoints({
  endpoints: builder => ({
    getIndividualRosterMember: builder.query<OrganizationMembersResponse, IRosterMemberQuery>({
      query: props => ({
        url: `${rosterBaseUrl}/${props.orgSk}/child-members?query=${props.indSk}&page=0`,
        method: 'GET'
      })
    }),
    getAllRosterMembers: builder.query<CreditTrackerViewModel, IRosterMemberQuery>({
      query: props => ({
        url: `${rosterBaseUrl}/${props.orgSk}/child-members?page=0`,
        method: 'GET'
      })
    }),
    getPaginatedRosterMembers: builder.query<OrganizationMembersResponse, IRosterMemberQuery>({
      query: props => {
        const baseUrl = `${creditTrackerAdminRosterBaseUrl}/${props.orgSk}`;
        const params = new URLSearchParams();

        params.set('page', String(props.page ?? 0));
        params.set('size', String(props.size ?? 20));
        params.set('query', props.query ?? '');

        if (props.jurisdictions?.length) {
          props.jurisdictions.forEach(jurisdiction => {
            params.append('jurisdictions', jurisdiction);
          });
        }

        if (props.indSk && props.indSk > 0) {
          params.set('individualSk', String(props.indSk));
        }

        return {
          url: `${baseUrl}?${params.toString()}`,
          method: 'GET'
        };
      }
    }),
    getUserTrackerData: builder.query<UserTrackerData[][], IUserTrackerDataQuery>({
      query: props => ({
        url: `${creditTrackerAdminRosterBaseUrl}/${props.orgSk}/tracker`,
        method: 'POST',
        body: props.userTrackerDataRequests
      })
    })
    // downloadDataForAllRosterMembers: builder.mutation<void, number>({
    //   query: orgSk => ({
    //     url: `${creditTrackerAdminRosterBaseUrl}/${orgSk}/excel`,
    //     params: { blobName: encodeURIComponent(`CreditTrackerData-${orgSk}.xlsx`) },
    //     method: 'GET',
    //     responseHandler: async response => {
    //       if (response.status <= 200) {
    //         const blob = await response.blob();
    //         processDownloadedFile(`CreditTrackerData-${orgSk}.xlsx`, blob);
    //       }
    //     },
    //     cache: 'no-cache',
    //     extraOptions: {
    //       skipTimeout: true
    //     } as baseQueryExtraOptions,
    //   })
    // })
  })
});

const creditTrackerAdminWithoutTimeoutEndpoints = ClientAPIWithoutTimeout.injectEndpoints({
  endpoints: builder => ({
    downloadDataForAllRosterMembers: builder.mutation<void, number>({
      query: orgSk => ({
        url: `${creditTrackerAdminRosterBaseUrl}/${orgSk}/excel`,
        params: { blobName: encodeURIComponent(`CreditTrackerData-${orgSk}.xlsx`) },
        method: 'GET',
        responseHandler: async response => {
          if (response.status <= 200) {
            const blob = await response.blob();
            processDownloadedFile(`CreditTrackerData-${orgSk}.xlsx`, blob);
          }
        },
        cache: 'no-cache'
      })
    })
  })
});

export const {
  useGetIndividualRosterMemberQuery,
  useGetAllRosterMembersQuery,
  useGetPaginatedRosterMembersQuery,
  useGetUserTrackerDataQuery
} = creditTrackerAdminEndpoints;

export const { useDownloadDataForAllRosterMembersMutation } =
  creditTrackerAdminWithoutTimeoutEndpoints;

export default creditTrackerAdminEndpoints;
